// Dream Vision — the images generated from dream entries.
//
// The images live in R2 and are never public: the list endpoint hands back short-lived signed URLs,
// so they must be fetched fresh rather than cached in localStorage or hard-coded into a src that
// outlives the token.

import { apiDelete, apiGet } from './client';

export interface Vision {
  id: string;
  /** The dream entry this came from, if it hasn't since been deleted. */
  entryId: string | null;
  /** Short-lived signed URL. Do not persist. */
  signedUrl: string;
  createdAt: string;
}

export async function fetchVisions(signal?: AbortSignal): Promise<Vision[]> {
  const res = await apiGet<{ items?: Vision[] }>('/api/dream-vision/list', signal);
  return res.items ?? [];
}

export async function deleteVision(id: string): Promise<void> {
  await apiDelete(`/api/dream-vision/${encodeURIComponent(id)}`);
}

/**
 * One of the house looks a vision can be rendered in.
 *
 * `preamble` is the literal text the Worker appends to the approved prompt, and it comes from the
 * server rather than the bundle for one reason: the approval step shows it to the user as the words
 * that will be added, and a copy kept here would eventually disagree with the words actually sent.
 * It is null for a style this account cannot generate with, which is why it is nullable rather than
 * optional — there is nothing to show, not merely nothing sent.
 */
export interface VisionStyle {
  id: string;
  name: string;
  premium: boolean;
  unlocked: boolean;
  preamble: string | null;
}

export interface VisionUsage {
  isPremium: boolean;
  /** Generations left this billing month. Always 0 for free accounts. */
  remainingMonthly: number;
  /** Rolling-24h safeguard, applied to premium and the intro generation alike. */
  remainingDaily: number;
  resetsAt: string | null;
  /** A free account that has never generated one gets a single introductory image. */
  introAvailable: boolean;
  /** Empty against a Worker that predates styles, so callers must handle knowing none. */
  styles: VisionStyle[];
  /** The style used when a generation names none. Null when the styles are unknown. */
  defaultStyleId: string | null;
}

export async function fetchVisionUsage(signal?: AbortSignal): Promise<VisionUsage> {
  const res = await apiGet<{
    premium?: boolean;
    remainingMonthly?: number;
    remainingDaily?: number;
    resetsAt?: string | null;
    introAvailable?: boolean;
    styles?: Array<{ id?: string; name?: string; premium?: boolean; unlocked?: boolean; preamble?: string | null }>;
    defaultStyle?: string | null;
  }>('/api/dream-vision/usage', signal);

  return {
    isPremium: res.premium === true,
    remainingMonthly: Number(res.remainingMonthly ?? 0),
    remainingDaily: Number(res.remainingDaily ?? 0),
    resetsAt: res.resetsAt ?? null,
    introAvailable: res.introAvailable === true,
    styles: (res.styles ?? []).map((style) => ({
      id: String(style.id ?? ''),
      name: String(style.name ?? ''),
      premium: style.premium === true,
      unlocked: style.unlocked === true,
      preamble: typeof style.preamble === 'string' ? style.preamble : null,
    })),
    defaultStyleId: res.defaultStyle ?? null,
  };
}

/**
 * The style a generation started right now would be rendered in, or null if that is not yet known.
 *
 * Null is a real answer and not a reason to guess: naming a style in the interface that the Worker
 * would not have used is worse than naming none, because the words shown alongside it are supposed
 * to be exactly the words that get sent.
 */
export function defaultVisionStyle(usage: VisionUsage | null): VisionStyle | null {
  if (!usage) return null;
  return usage.styles.find((style) => style.id === usage.defaultStyleId) ?? null;
}

/** True when the account may start a generation right now. */
export function canGenerate(usage: VisionUsage): boolean {
  if (usage.remainingDaily <= 0) return false;
  return usage.isPremium ? usage.remainingMonthly > 0 : usage.introAvailable;
}
